import React, { memo } from 'react'

import Image from 'next/image'

import {
  AccountBalance,
  ArrowForward,
  Badge,
  DirectionsCar,
  ElectricBolt,
  Gavel,
  Groups,
  Hub,
  LocalHospital,
  PeopleAlt,
  RocketLaunch,
  School,
  Speed,
} from '@mui/icons-material'
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Fade,
  Grid,
  Paper,
  Stack,
  Typography,
  Zoom,
  alpha,
  useTheme,
} from '@mui/material'

import { shimmer } from '@/utils/animations'

interface HeroSectionProps {
  onStaffPortalClick: () => void
  mounted: boolean
}

const heroStats = [
  {
    label: 'Citizens Enrolled',
    value: '12.4M',
    change: '+284K',
    changeLabel: 'today',
    icon: <Groups />,
    color: '#1E3A5F',
  },
  {
    label: 'System Uptime',
    value: '99.98%',
    change: '+0.03%',
    changeLabel: 'this month',
    icon: <ElectricBolt />,
    color: '#059669',
  },
  {
    label: 'Active Services',
    value: '47',
    change: '+3',
    changeLabel: 'new',
    icon: <Hub />,
    color: '#3B82F6',
  },
  {
    label: 'Response Time',
    value: '23ms',
    change: '-15ms',
    changeLabel: 'faster',
    icon: <Speed />,
    color: '#F59E0B',
  },
]

const serviceIntegrations = [
  { name: 'Banking Services', icon: <AccountBalance /> },
  { name: 'Healthcare', icon: <LocalHospital /> },
  { name: 'Education', icon: <School /> },
  { name: 'Transport', icon: <DirectionsCar /> },
  { name: 'Tax Services', icon: <Gavel /> },
  { name: 'Social Services', icon: <PeopleAlt /> },
]

const HeroSection = memo<HeroSectionProps>(({ onStaffPortalClick, mounted }) => {
  const theme = useTheme()

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 4, md: 8 } }}>
      <Grid container alignItems="center" spacing={6}>
        {/* Left Content */}
        <Grid size={{ xs: 12, lg: 7 }}>
          <Fade in={mounted} timeout={800}>
            <Box>
              <Chip
                icon={<RocketLaunch />}
                label="ETHIOPIA'S DIGITAL FUTURE"
                sx={{
                  mb: 4,
                  py: 2.5,
                  px: 2,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: 'white',
                  border: 'none',
                }}
              />

              <Stack direction="row" spacing={4} alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 4,
                    height: 100,
                    background: 'linear-gradient(180deg, #009639 0%, #FCDD09 50%, #DA121A 100%)',
                    borderRadius: 2,
                  }}
                />
                <Typography
                  variant="h2"
                  fontWeight={800}
                  sx={{
                    fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem', xl: '3.5rem' },
                    lineHeight: 1.2,
                    color: theme.palette.primary.main,
                  }}
                >
                  Ethiopian Digital
                  <br />
                  Identity Portal
                </Typography>
              </Stack>

              <Typography
                variant="h5"
                sx={{
                  mb: 5,
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  maxWidth: 600,
                }}
              >
                Empowering 130 million Ethiopians with secure, verifiable digital identities.
                Building the foundation for a connected, prosperous digital economy.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
                <Button
                  variant="contained"
                  size="large"
                  onClick={onStaffPortalClick}
                  endIcon={<ArrowForward />}
                  sx={{
                    px: 5,
                    py: 2.5,
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    boxShadow: theme.shadows[4],
                    '&:hover': {
                      boxShadow: theme.shadows[8],
                      '& .MuiButton-endIcon': {
                        transform: 'translateX(4px)',
                      },
                    },
                  }}
                >
                  Staff Portal Access
                </Button>

                <Stack direction="row" spacing={2} alignItems="center">
                  <AvatarGroup max={4}>
                    {[...Array(4)].map((_, i) => (
                      <Avatar
                        key={i}
                        src={`/assets/images/ethpass_avatar.png`}
                        alt={`User ${i + 1}`}
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          width: 44,
                          height: 44,
                          fontSize: '0.875rem',
                          border: `2px solid ${theme.palette.background.default}`,
                        }}
                      >
                        {String.fromCharCode(65 + i)}
                      </Avatar>
                    ))}
                  </AvatarGroup>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      3,847 Staff Online
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Across 14 regions
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Fade>
        </Grid>

        {/* Right Visual */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <Box
            sx={{
              position: 'relative',
              height: { xs: 300, md: 400 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* ID Card Visual */}
            <Zoom in={mounted} timeout={1000}>
              <Card
                elevation={8}
                sx={{
                  width: 360,
                  height: 227,
                  borderRadius: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  background: theme.palette.background.paper,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px) rotateY(5deg)',
                    boxShadow: theme.shadows[20],
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.3) 50%, transparent 70%)',
                    backgroundSize: '200% 100%',
                    animation: `${shimmer} 3s infinite`,
                    zIndex: 1,
                  },
                }}
              >
                {/* ID Card Image */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Image
                    src="/assets/images/fayda_id.jpg"
                    alt="Ethiopian Digital ID Card"
                    fill
                    style={{ objectFit: 'cover' }}
                    priority
                  />

                  {/* Overlay for premium effect */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(135deg, transparent 0%, rgba(30, 58, 95, 0.05) 100%)',
                      pointerEvents: 'none',
                    }}
                  />
                </Box>

                {/* Security Badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    right: 16,
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 1,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    zIndex: 2,
                  }}
                >
                  <Badge sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                  <Typography variant="caption" fontWeight={600} color="primary.main">
                    Secure ID
                  </Typography>
                </Box>
              </Card>
            </Zoom>

            {/* Service Icons */}
            <Box sx={{ position: 'absolute', inset: 0 }}>
              {serviceIntegrations.map((service, index) => {
                const angle = (index * 360) / 6
                const radius = 180
                const x = Math.cos((angle * Math.PI) / 180) * radius
                const y = Math.sin((angle * Math.PI) / 180) * radius

                return (
                  <Fade key={index} in={mounted} timeout={1200 + index * 100}>
                    <Paper
                      elevation={2}
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: theme.palette.background.paper,
                        color: theme.palette.primary.main,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(1.1)`,
                          boxShadow: theme.shadows[6],
                          bgcolor: theme.palette.primary.main,
                          color: 'white',
                        },
                      }}
                    >
                      {service.icon}
                    </Paper>
                  </Fade>
                )
              })}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Hero Stats */}
      <Grid container spacing={3} sx={{ mt: 6 }}>
        {heroStats.map((stat, index) => (
          <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
            <Fade in={mounted} timeout={800 + index * 100}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[4],
                    borderColor: alpha(stat.color, 0.3),
                  },
                }}
              >
                <Stack spacing={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: alpha(stat.color, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: stat.color,
                    }}
                  >
                    {stat.icon}
                  </Box>

                  <Box>
                    <Typography variant="h4" fontWeight={800}>
                      {stat.value}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        {stat.label}
                      </Typography>
                      <Chip
                        label={`${stat.change} ${stat.changeLabel}`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.main,
                        }}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Fade>
          </Grid>
        ))}
      </Grid>
    </Container>
  )
})

HeroSection.displayName = 'HeroSection'

export default HeroSection
